import { useState, type Ref } from "react";
import clsx from "clsx";
import type { ProjectNote } from "@/lib/projectOverview";
import { resolveReferencePositions, splitNoteBodySegments } from "@/projectOverview/notes/noteReferences";
import type { SystemNoteFact, ParsedSystemNote } from "@/projectOverview/notes/systemNote";
import type { NoteRefType } from "@/projectOverview/notes/types";

const BULLET_PREVIEW = 3;

function fmtEventTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function FactText({
  value,
  fullText,
  references,
  onRefClick,
}: {
  value: string;
  fullText: string;
  references: ProjectNote["references"];
  onRefClick: (type: NoteRefType | string, id: string, label: string) => void;
}) {
  const local = resolveReferencePositions(fullText, references || []).flatMap((ref) => {
    const token = fullText.slice(ref.start, ref.end);
    if (!token) return [];
    const at = value.indexOf(token);
    if (at < 0) return [];
    return [{ ...ref, start: at, end: at + token.length }];
  });
  const segments = splitNoteBodySegments(value, local);
  return (
    <>
      {segments.map((seg, idx) => {
        if (seg.kind === "ref" && seg.ref) {
          return (
            <button
              key={`ref-${idx}-${seg.ref.start}`}
              type="button"
              className="eh-po-chat-ref"
              onClick={() => onRefClick(seg.ref!.type, seg.ref!.ref_id, seg.ref!.label || seg.text)}
            >
              {seg.text}
            </button>
          );
        }
        return <span key={`t-${idx}`}>{seg.text}</span>;
      })}
    </>
  );
}

function FactValue({
  fact,
  fullText,
  references,
  onRefClick,
}: {
  fact: SystemNoteFact;
  fullText: string;
  references: ProjectNote["references"];
  onRefClick: (type: NoteRefType | string, id: string, label: string) => void;
}) {
  if (fact.kind === "change" && fact.from && fact.to) {
    return (
      <>
        <span className="eh-po-note-event-from">
          <FactText
            value={fact.from}
            fullText={fullText}
            references={references}
            onRefClick={onRefClick}
          />
        </span>
        <span className="eh-po-note-event-arrow" aria-hidden>
          →
        </span>
        <span className="eh-po-note-event-to">
          <FactText
            value={fact.to}
            fullText={fullText}
            references={references}
            onRefClick={onRefClick}
          />
        </span>
      </>
    );
  }
  return (
    <FactText value={fact.value} fullText={fullText} references={references} onRefClick={onRefClick} />
  );
}

function EntryList({
  facts,
  fullText,
  references,
  onRefClick,
}: {
  facts: SystemNoteFact[];
  fullText: string;
  references: ProjectNote["references"];
  onRefClick: (type: NoteRefType | string, id: string, label: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const hidden = facts.length > BULLET_PREVIEW && !open;
  const visible = hidden ? facts.slice(0, BULLET_PREVIEW) : facts;
  const rest = facts.length - visible.length;
  return (
    <div className="eh-po-note-event-entries">
      <ul className="eh-po-note-event-list">
        {visible.map((fact, index) => (
          <li key={`${fact.sourceStart}-${index}`}>
            <FactText
              value={fact.value}
              fullText={fullText}
              references={references}
              onRefClick={onRefClick}
            />
          </li>
        ))}
      </ul>
      {facts.length > BULLET_PREVIEW ? (
        <button type="button" className="eh-po-note-event-more" onClick={() => setOpen((value) => !value)}>
          {hidden
            ? `${rest} weitere ${rest === 1 ? "Buchung" : "Buchungen"}`
            : "Weniger anzeigen"}
        </button>
      ) : null}
    </div>
  );
}

type EventRow =
  | { type: "fact"; fact: SystemNoteFact }
  | { type: "bullets"; facts: SystemNoteFact[] };

function groupFacts(facts: SystemNoteFact[]): EventRow[] {
  const rows: EventRow[] = [];
  for (const fact of facts) {
    if (fact.kind === "bullet") {
      const last = rows[rows.length - 1];
      if (last?.type === "bullets") last.facts.push(fact);
      else rows.push({ type: "bullets", facts: [fact] });
    } else {
      rows.push({ type: "fact", fact });
    }
  }
  return rows;
}

export function SystemNoteCard({
  note,
  event,
  own,
  highlighted,
  cardRef,
  onDelete,
  onRefClick,
}: {
  note: ProjectNote;
  event: ParsedSystemNote;
  own: boolean;
  highlighted: boolean;
  cardRef?: Ref<HTMLElement>;
  onDelete: () => void;
  onRefClick: (type: NoteRefType | string, id: string, label: string) => void;
}) {
  const author = note.created_by_display || note.created_by_login || "System";
  const when = fmtEventTime(note.created_at);
  const fullText = String(note.note || "");
  const rows = groupFacts(event.facts);

  return (
    <article
      ref={cardRef}
      className={clsx(
        "eh-po-note-event",
        note.visibility === "buero" && "eh-po-note-event--buero",
        note._pending && "opacity-70",
        highlighted && "eh-po-chat-bubble--highlight",
      )}
    >
      <header className="eh-po-note-event-head">
        <h3 className="eh-po-note-event-title">{event.title}</h3>
        <p className="eh-po-note-event-meta">
          <span>{author}</span>
          {when ? <time dateTime={note.created_at || undefined}>{when}</time> : null}
          {note.visibility === "buero" ? <span className="eh-po-chat-badge">Nur Büro</span> : null}
        </p>
        {own && !note._pending ? (
          <button
            type="button"
            className="eh-po-chat-delete"
            title="Eigene Notiz löschen"
            aria-label="Eigene Notiz löschen"
            onClick={onDelete}
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <path
                fill="currentColor"
                d="M9 3h6l1 2h4v2H4V5h4l1-2zm-2 6h2v9H7V9zm4 0h2v9h-2V9zm4 0h2v9h-2V9zM6 7h12l-1 14H7L6 7z"
              />
            </svg>
          </button>
        ) : null}
      </header>
      {rows.length ? (
        <div className="eh-po-note-event-body">
          {rows.map((row, index) => {
            if (row.type === "bullets") {
              return (
                <EntryList
                  key={`b-${index}`}
                  facts={row.facts}
                  fullText={fullText}
                  references={note.references}
                  onRefClick={onRefClick}
                />
              );
            }
            const fact = row.fact;
            const stacked = fact.kind === "prose" || fact.value.length > 72;
            if (fact.kind === "prose") {
              return (
                <p key={`p-${fact.sourceStart}-${index}`} className="eh-po-note-event-prose">
                  <FactValue
                    fact={fact}
                    fullText={fullText}
                    references={note.references}
                    onRefClick={onRefClick}
                  />
                </p>
              );
            }
            return (
              <div
                key={`f-${fact.sourceStart}-${index}`}
                className={clsx("eh-po-note-event-row", stacked && "eh-po-note-event-row--stack")}
              >
                <div className="eh-po-note-event-label">{fact.label}</div>
                <div className="eh-po-note-event-value">
                  <FactValue
                    fact={fact}
                    fullText={fullText}
                    references={note.references}
                    onRefClick={onRefClick}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </article>
  );
}
